import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Client, MeterAvailabilityDataIn, MeterAvailabilityDataOut } from '../../../../../api/api-reference';

type SlotStatus = 'online' | 'offline';

@Component({
  selector: 'app-online-hours',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './online-hours.component.html',
  styleUrl: './online-hours.component.scss',
})
export class OnlineHoursComponent implements OnInit {
  @Input() meterId = '';
  @Output() rangeWindowChange = new EventEmitter<MeterAvailabilityDataIn>();

  ranges = ['1H', '8H', '1D', '3D', '7D', '1M'];
  activeRange = '1H';
  currentTime = this.formatCurrentTime();
  // startLabel is this slot's own start-boundary label (attached to its own cell, anchored
  // to that cell's left edge - no separate measurement needed, it's the cell's own edge)
  slots: { status: SlotStatus; time: Date; endTime: Date; startLabel: string[] }[] = [];
  // the one boundary with no "next slot" to be a start label for - the end of the last slot,
  // attached to the last cell's right edge instead
  lastSlotEndLabel: string[] = [];
  slotSizeLabel = '';

  // index of the slot currently under the cursor - null hides the crosshair. Set via
  // (mouseenter) on each .oh-cell, so the crosshair is a child of that cell and anchored to
  // its left edge, exactly like a start label - no pixel math needed to place it
  hoveredSlotIndex: number | null = null;

  // placeholder — meter's configured reporting interval in minutes; to be wired to real device data
  intervalMinutes = 15;

  // ── Slot size — edit this to change how much time one slot represents ──
  // 1H / 8H aren't listed here: their slot size is the meter's own reporting
  // interval (`intervalMinutes` above), not a fixed bucket.
  private readonly bucketMinutesByRange: Record<string, number> = {
    '1D': 60,       // 1 slot = 1 hour
    '3D': 6 * 60,   // 1 slot = 6 hours
    '7D': 12 * 60,  // 1 slot = 12 hours
    '1M': 24 * 60,  // 1 slot = 1 day
  };

  // max number of boundaries that get a text label for ranges with many slots - the tick
  // (thin line) still marks every boundary, only the label is thinned out. Not listed here
  // ('1H') means every boundary is labeled, since there are few enough of them.
  private readonly maxLabelsByRange: Record<string, number> = {
    '8H': 9,
    '1D': 9,
    '3D': 7,
    '7D': 8,
    '1M': 8,
  };

  constructor(private client: Client, private toastr: ToastrService) {}

  ngOnInit(): void {
    this.updateRangeData();
  }

  isDisabled(range: string): boolean {
    if (range === '1H') return this.intervalMinutes > 60;
    if (range === '8H') return this.intervalMinutes > 8 * 60;
    return false;
  }

  selectRange(range: string) {
    if (this.isDisabled(range)) return;
    this.activeRange = range;
    this.updateRangeData();
  }

  private bucketMinutesFor(range: string): number {
    if (range === '1H' || range === '8H') return this.intervalMinutes;
    return this.bucketMinutesByRange[range];
  }

  private rangeDurationMinutesFor(range: string): number {
    switch (range) {
      case '1H': return 60;
      case '8H': return 8 * 60;
      case '1D': return 24 * 60;
      case '3D': return 3 * 24 * 60;
      case '7D': return 7 * 24 * 60;
      case '1M': return this.daysInCurrentMonth() * 24 * 60;
      default: return 0;
    }
  }

  private daysInCurrentMonth(): number {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  }

  // 1H/8H buckets already match the meter's raw reporting interval, so the backend
  // is queried at that resolution directly. 1D/3D/7D/1M buckets (6h/12h/1day) are
  // coarser than the backend's max interval (60 min), so we query at 60 min and
  // merge consecutive raw slots into one displayed bucket in aggregateSlots().
  private backendIntervalFor(range: string): number {
    if (range === '1H' || range === '8H') return this.intervalMinutes;
    return 60;
  }

  private aggregateSlots(rawSlots: MeterAvailabilityDataOut[], groupSize: number): { status: SlotStatus; time: Date; endTime: Date }[] {
    const result: { status: SlotStatus; time: Date; endTime: Date }[] = [];
    for (let i = 0; i < rawSlots.length; i += groupSize) {
      const group = rawSlots.slice(i, i + groupSize);
      if (group.length === 0) continue;
      result.push({
        status: group.some((s) => s.online) ? 'online' : 'offline',
        time: group[0].startTime!,
        endTime: group[group.length - 1].endTime!,
      });
    }
    return result;
  }

  // grid-template-columns for .oh-bar - odd tracks are the N slot cells (1fr each), even
  // tracks are the (N-1) 3px gaps between them
  get barGridTemplateColumns(): string {
    const n = this.slots.length;
    if (n === 0) return 'none';
    return Array.from({ length: 2 * n - 1 }, (_, i) => (i % 2 === 0 ? '1fr' : '3px')).join(' ');
  }

  // attaches a label to each slot's own start boundary (n slots -> n start labels), plus a
  // separate label for the one boundary with no slot to start - the end of the last slot.
  // No pixel measurement anywhere: a label lives inside the very cell whose edge it marks,
  // so it's positioned by that cell's own box, not by a calculation that has to match it.
  private attachBoundaryLabels(slots: { status: SlotStatus; time: Date; endTime: Date }[]): { status: SlotStatus; time: Date; endTime: Date; startLabel: string[] }[] {
    const n = slots.length;
    if (n === 0) {
      this.lastSlotEndLabel = [];
      return [];
    }

    const boundaryTimes = [...slots.map((s) => s.time), slots[n - 1].endTime];
    const showDate = this.spansMultipleDays(boundaryTimes[0], boundaryTimes[n]);
    const labeledBoundaries = this.pickLabeledBoundaries(n);

    this.lastSlotEndLabel = labeledBoundaries.has(n) ? this.formatAxisLabel(boundaryTimes[n], showDate) : [];

    return slots.map((s, k) => ({
      ...s,
      startLabel: labeledBoundaries.has(k) ? this.formatAxisLabel(boundaryTimes[k], showDate) : [],
    }));
  }

  // which of the n+1 boundary indices get a text label - always the first (0) and last (n),
  // plus evenly-spaced ones in between up to the range's cap. No cap means all of them.
  private pickLabeledBoundaries(n: number): Set<number> {
    const maxLabels = this.maxLabelsByRange[this.activeRange];
    if (!maxLabels || maxLabels >= n + 1) {
      return new Set(Array.from({ length: n + 1 }, (_, i) => i));
    }

    const indices = new Set<number>();
    for (let i = 0; i < maxLabels; i++) {
      indices.add(Math.round((i / (maxLabels - 1)) * n));
    }
    return indices;
  }

  // true when the first and last displayed slot fall on different calendar days (in the
  // configured tz), so axis labels can call out the day change instead of just showing am/pm
  private spansMultipleDays(first: Date, last: Date): boolean {
    const offsetMs = Number(localStorage.getItem('tz') ?? 0) * 60000;
    const a = new Date(first.getTime() + offsetMs);
    const b = new Date(last.getTime() + offsetMs);
    return a.getUTCFullYear() !== b.getUTCFullYear() || a.getUTCMonth() !== b.getUTCMonth() || a.getUTCDate() !== b.getUTCDate();
  }

  // returns the label as separate display lines - [monthDay, time] when a date is needed,
  // so they stack on their own lines instead of wrapping mid-word if squeezed for space.
  // Always shows the exact hour:minute of the boundary as returned by BE, for every range -
  // even a daily/12h/6h bucket's boundary isn't necessarily on the hour (it's "now" minus
  // whole buckets), so rounding it off would hide real information.
  private formatAxisLabel(date: Date, showDate: boolean): string[] {
    const offsetMs = Number(localStorage.getItem('tz') ?? 0) * 60000;
    const shifted = new Date(date.getTime() + offsetMs);

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthDay = `${months[shifted.getUTCMonth()]} ${shifted.getUTCDate()}`;

    let hours = shifted.getUTCHours();
    const period = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    const minutes = shifted.getUTCMinutes().toString().padStart(2, '0');
    const time = `${hours}:${minutes}${period}`;

    return showDate ? [monthDay, time] : [time];
  }

  private updateRangeData(): void {
    this.currentTime = this.formatCurrentTime();

    const bucketMinutes = this.bucketMinutesFor(this.activeRange);
    this.slotSizeLabel = this.formatDuration(bucketMinutes);

    const backendInterval = this.backendIntervalFor(this.activeRange);
    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - this.rangeDurationMinutesFor(this.activeRange) * 60000);

    const dataIn = new MeterAvailabilityDataIn({
      meterId: this.meterId,
      interval: backendInterval,
      startTime,
      endTime,
    });

    this.rangeWindowChange.emit(dataIn);

    this.client
      .getMeterAvailabilityForInterval(dataIn)
      .subscribe({
        next: (rawSlots) => {
          const slots = this.aggregateSlots(rawSlots ?? [], bucketMinutes / backendInterval);
          this.slots = this.attachBoundaryLabels(slots);
          this.hoveredSlotIndex = null;
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
  }

  onCellMouseEnter(index: number): void {
    this.hoveredSlotIndex = index;
  }

  onBarMouseLeave(): void {
    this.hoveredSlotIndex = null;
  }

  get hoveredSlotLabel(): string {
    return this.hoveredSlotIndex === null ? '' : this.formatSlotTip(this.slots[this.hoveredSlotIndex].time);
  }

  private formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    if (minutes < 24 * 60) return `${minutes / 60} h`;
    const days = minutes / (24 * 60);
    return days === 1 ? '1 day' : `${days} days`;
  }

  private formatSlotTip(date: Date): string {
    const offsetMs = Number(localStorage.getItem('tz') ?? 0) * 60000;
    const shifted = new Date(date.getTime() + offsetMs);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const datePart = `${months[shifted.getUTCMonth()]} ${shifted.getUTCDate()}, ${shifted.getUTCFullYear()}`;
    let hours = shifted.getUTCHours();
    const period = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    const minutes = shifted.getUTCMinutes().toString().padStart(2, '0');
    return `${datePart} ${hours.toString().padStart(2, '0')}:${minutes}${period}`;
  }

  private formatCurrentTime(): string {
    const offsetMs = Number(localStorage.getItem('tz') ?? 0) * 60000;
    const shifted = new Date(new Date().getTime() + offsetMs);
    let hours = shifted.getUTCHours();
    const minutes = shifted.getUTCMinutes().toString().padStart(2, '0');
    const seconds = shifted.getUTCSeconds().toString().padStart(2, '0');
    const period = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    return `${hours.toString().padStart(2, '0')}:${minutes}:${seconds}${period}`;
  }
}
